import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { NavBar, Card, Button } from 'antd-mobile';
import { LeftOutline, FileOutline } from 'antd-mobile-icons';
import { useI18n } from '@/hooks/useI18n';

export default function ServiceAgreement() {
  const navigate = useNavigate();
  const { t } = useI18n();

  const agreementSections = [
    {
      title: t('服务范围'),
      content: t('本服务为用户提供AI智能助手功能，包括但不限于智能对话、文档处理、数据分析等服务。')
    },
    {
      title: t('用户权利'),
      content: t('用户有权使用本服务的所有功能，享受技术支持，并对个人数据拥有控制权。')
    },
    {
      title: t('用户义务'),
      content: t('用户应遵守相关法律法规，不得利用本服务从事违法活动，并有义务保护账户安全。')
    },
    {
      title: t('知识产权'),
      content: t('本服务的所有知识产权归公司所有，用户在使用过程中产生的内容归用户所有。')
    },
    {
      title: t('数据保护'),
      content: t('我们承诺保护用户隐私，严格按照隐私政策处理用户数据，不会未经授权泄露用户信息。')
    },
    {
      title: t('责任限制'),
      content: t('在法律允许的范围内，公司对因使用本服务而产生的任何损失不承担责任。')
    },
    {
      title: t('协议终止'),
      content: t('用户可随时终止使用本服务，公司也可在用户违反协议时终止服务。')
    },
    {
      title: t('协议变更'),
      content: t('公司有权随时修改本协议，修改后的协议将在网站上公布并立即生效。')
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex justify-center">
      <div className="w-full min-h-screen relative overflow-hidden" style={{ maxWidth: '480px' }}>
      {/* 背景装饰 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-400/20 to-pink-400/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-cyan-400/10 to-blue-400/10 rounded-full blur-3xl" />
      </div>

      {/* 顶部导航 */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <NavBar 
          onBack={() => navigate('/profile')}
          className="bg-white/80 backdrop-blur-xl border-b border-gray-200/50"
        >
          <div className="flex items-center text-gray-800">
            <FileOutline className="mr-2" />
            {t('服务协议')}
          </div>
        </NavBar>
      </motion.div>

      <div className="p-4 relative z-10">
        {/* 协议标题 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Card className="bg-white/80 backdrop-blur-xl border-0 shadow-xl rounded-2xl overflow-hidden">
            <motion.div 
              className="p-8 text-center"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3, type: "spring", stiffness: 200 }}
                className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
              >
                <FileOutline className="text-white text-2xl" />
              </motion.div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">
                {t('服务协议')}
              </h1>
              <p className="text-gray-500 text-sm">
                {t('最后更新')}: 2024年1月1日
              </p>
            </motion.div>
          </Card>
        </motion.div>

        {/* 协议内容 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="mt-4"
        >
          <Card className="bg-white/80 backdrop-blur-xl border-0 shadow-xl rounded-2xl overflow-hidden">
            <div className="p-6">
              <motion.div 
                className="mb-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {t('欢迎使用我们的服务！在使用本服务前，请仔细阅读以下服务协议。使用本服务即表示您同意遵守本协议的所有条款。')}
                  </p>
                </div>
              </motion.div>

              <div className="space-y-6">
                {agreementSections.map((section, index) => (
                  <motion.div 
                    key={index}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.4 + index * 0.1 }}
                    whileHover={{ x: 4 }}
                    className="p-4 bg-gradient-to-r from-gray-50/50 to-gray-100/50 rounded-xl border border-gray-200 hover:border-blue-200 transition-all duration-200"
                  >
                    <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                      <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center mr-3 text-white text-sm font-bold">
                        {index + 1}
                      </div>
                      {section.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed pl-9">
                      {section.content}
                    </p>
                  </motion.div>
                ))}
              </div>

              {/* 联系我们 */}
              <motion.div 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.8 }}
                className="mt-8 p-4 bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl border border-gray-200"
              >
                <h4 className="text-base font-semibold text-gray-800 mb-2 flex items-center">
                  <div className="w-1 h-5 bg-gradient-to-b from-gray-500 to-slate-500 rounded-full mr-3" />
                  {t('联系我们')}
                </h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {t('如果您对本协议有任何疑问，请通过邮箱 support@zevolve.ai 或客服热线 400-123-4567 联系我们。')}
                </p>
              </motion.div>
            </div>
          </Card>
        </motion.div>

        {/* 操作按钮 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.9 }}
          className="mt-6"
        >
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              color="primary"
              block
              onClick={() => navigate('/profile')}
              className="bg-gradient-to-r from-blue-500 to-indigo-500 border-0 rounded-xl h-12 font-medium shadow-lg"
            >
              {t('返回')}
            </Button>
          </motion.div>
        </motion.div>

        {/* 底部间距 */}
        <div className="h-8" />
      </div>
      </div>
    </div>
  );
}